"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import { workScopeStatusOptions } from "../../constants/options"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import ProjectGeneralNotesWithMentions from "./project-general-notes-with-mentions"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useState, useMemo } from "react"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import { Input } from "@/shared/components/ui/input"
import { Button } from "@/shared/components/ui/button"
import { Label } from "@/shared/components/ui/label"
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/shared/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Search, Info } from "lucide-react"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const baseFieldName = "home.miscellaneousInfo"

function HomeMiscellaneousForm() {
    const { session } = useSession()
    const isVolunteer = session?.role === UserRole.VOLUNTEER
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    
    // Watch status fields and claimed by fields
    const miscellaneous = useWatch({ control, name: `${baseFieldName}.miscellaneous` })
    const miscellaneousClaimedBy = useWatch({ control, name: `${baseFieldName}.claimedBy` })
    const miscDescription = useWatch({ control, name: `${baseFieldName}.miscDescription`, defaultValue: "" })
    
    // Keyword filter for Miscellaneous Description (same as Project General Notes)
    const [miscDescFilterKeyword, setMiscDescFilterKeyword] = useState("")
    const [miscDescFilterDialogOpen, setMiscDescFilterDialogOpen] = useState(false)
    const [miscDescInfoPopoverOpen, setMiscDescInfoPopoverOpen] = useState(false)
    
    const MISC_DESC_INFO = "Use this field to add dated notes. Press Enter to start a new line and a timestamp with your name will be added automatically. Typing @ tags a volunteer for a notification. Use keywords such as Shed, Deck Rebuild, Deck Repair, Ramp, etc."
    
    const miscDescNotesLines = useMemo(() => {
        const text = miscDescription ?? ""
        return text.split("\n").filter((line) => line.trim().length > 0)
    }, [miscDescription])
    
    const miscDescFilteredLines = useMemo(() => {
        if (!miscDescFilterKeyword.trim()) return miscDescNotesLines
        const kw = miscDescFilterKeyword.trim().toLowerCase()
        return miscDescNotesLines.filter((line) => line.toLowerCase().includes(kw))
    }, [miscDescNotesLines, miscDescFilterKeyword])
    
    const isMiscDescFilterActive = miscDescFilterKeyword.trim().length > 0
    
    const miscDescLabelAction = (
        <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground shrink-0">
                Type @ to tag a notification
            </span>
            <Popover open={miscDescInfoPopoverOpen} onOpenChange={setMiscDescInfoPopoverOpen}>
                <TooltipProvider delayDuration={300}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                                    aria-label="Miscellaneous Description info"
                                >
                                    <Info className="h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                            <p>{MISC_DESC_INFO}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <PopoverContent side="bottom" className="max-w-[320px] text-sm">
                    <p>{MISC_DESC_INFO}</p>
                </PopoverContent>
            </Popover>
            <Dialog open={miscDescFilterDialogOpen} onOpenChange={setMiscDescFilterDialogOpen}>
            <DialogTrigger asChild>
                <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="shrink-0 bg-primary hover:bg-primary/90 text-white font-bold h-6 w-8 rounded-full p-0"
                    aria-label="Filter notes by keyword"
                >
                    <Search className="h-4 w-4" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-lg">
                <DialogTitle>Filter notes by keyword</DialogTitle>
                <div className="space-y-4 overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
                    <div className="space-y-2">
                        <Label htmlFor="misc-desc-filter-popup" className="text-sm">Keyword</Label>
                        <Input
                            id="misc-desc-filter-popup"
                            type="text"
                            placeholder="Type to filter notes..."
                            value={miscDescFilterKeyword}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMiscDescFilterKeyword(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {isMiscDescFilterActive && (
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">
                                {miscDescFilteredLines.length === 0 ? (
                                    <>No notes match &quot;{miscDescFilterKeyword.trim()}&quot;</>
                                ) : (
                                    <>Showing {miscDescFilteredLines.length} of {miscDescNotesLines.length} note{miscDescNotesLines.length !== 1 ? "s" : ""} matching &quot;{miscDescFilterKeyword.trim()}&quot;</>
                                )}
                            </p>
                            {miscDescFilteredLines.length > 0 && (
                                <div className="rounded-md border border-input bg-muted/30 p-3 max-h-64 overflow-y-auto">
                                    <pre className="whitespace-pre-wrap text-sm font-sans">{miscDescFilteredLines.join("\n")}</pre>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="flex items-center gap-2 pt-2">
                        {isMiscDescFilterActive && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => setMiscDescFilterKeyword("")}>
                                Clear filter
                            </Button>
                        )}
                        <Button type="button" variant="default" size="sm" onClick={() => setMiscDescFilterDialogOpen(false)} className="ml-auto">
                            Done
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
        </div>
    )
    
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: miscellaneous,
        claimedBy: miscellaneousClaimedBy,
        claimedByFieldPath: `${baseFieldName}.claimedBy`,
        setValue,
        getValues,
    })
    return (
        <HomeDetailsWrapperAccordion 
            heading="Miscellaneous Goodwill"
            wrapperClassName="xl:grid-cols-2"
        >
            <FormSelectInput
                inputGroupProps={{
                    label: "Miscellaneous Goodwill",
                    labelStyle: goodwillLabelStyle,
                }}
                options={workScopeStatusOptions}
                name={`${baseFieldName}.miscellaneous`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{ label: "Claimed By" }}
                options={claimedByOptions}
                name={`${baseFieldName}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            <div className="xl:col-span-2">
                <ProjectGeneralNotesWithMentions
                    name={`${baseFieldName}.miscDescription`}
                    control={control}
                    defaultRows={4}
                    readOnly={isVolunteer}
                    inputGroupProps={{
                        label: "Miscellaneous Description",
                        wrapperClassName: "xl:col-span-2",
                    }}
                    labelAction={isVolunteer ? undefined : miscDescLabelAction}
                />
            </div>
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeMiscellaneousForm
